import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, BookOpen, ExternalLink, LogOut, Menu, MessageSquare, Moon, PanelLeft, Search, Settings, Sun, User, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { DirtyProvider, useDirty } from '../../lib/dirty';
import { NAV, reviewBadgeTone, titleForUrl } from '../../lib/nav';
import { apiGet } from '../../lib/api-client';
import { ActivityProvider, useActivity } from '../ui/Activity';
import { CrumbTrail, PageChromeProvider, usePageCrumbs } from '../ui/chrome';
import { nextNavMode, readNavMode, writeNavMode, type NavMode } from '../../lib/nav-mode';
import { CollaborateDock } from '../collaborate/CollaborateDock';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { DropdownMenu } from '../ui/DropdownMenu';

export function Layout({ children }: { children: ReactNode }) {
  return (
    <DirtyProvider>
      <ActivityProvider>
        <PageChromeProvider>
          <Shell>{children}</Shell>
        </PageChromeProvider>
      </ActivityProvider>
    </DirtyProvider>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const { user, can, logout, collaborateOpen, openCollaborate, closeCollaborate } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dirty = useDirty();
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const [navMode, setNavMode] = useState<NavMode>(() => readNavMode());
  const [dark, setDark] = useState(() => localStorage.getItem('bo-theme') !== 'maxiron-light');
  const [query, setQuery] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const notesRef = useRef<HTMLDivElement>(null);
  const sections = NAV.map((section) => ({ ...section, items: section.items.filter((item) => can(item.page) && item.path !== '/settings') })).filter((section) => section.items.length);
  const settingsItem = NAV.flatMap((section) => section.items).find((item) => item.path === '/settings' && can(item.page));
  const review = useQuery({
    queryKey: ['review'],
    queryFn: () => apiGet<unknown[]>('/api/review'),
    enabled: can('finance_home'),
  });
  const assigned = Array.isArray(review.data) ? review.data.length : 0;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.classList.toggle('bo', dark);
  }, [dark]);

  useEffect(() => {
    if (!notesOpen) return;
    function close(event: MouseEvent) {
      if (!notesRef.current?.contains(event.target as Node)) setNotesOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [notesOpen]);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    localStorage.setItem('bo-theme', next ? 'maxiron-dark' : 'maxiron-light');
  }

  function cycleNav() {
    const next = nextNavMode(navMode);
    setNavMode(next);
    writeNavMode(next);
    localStorage.setItem('bo-nav-user', '1');
    setPeek(false);
  }

  function go(path: string) {
    setOpen(false);
    setPeek(false);
    dirty.requestLeave(() => navigate(path));
  }

  const links = (iconsOnly: boolean) => (
    <nav className={`min-h-0 flex-1 space-y-4 overflow-y-auto ${iconsOnly ? 'p-2' : 'p-4'}`}>
      {sections.map((section) => (
        <div key={section.title} className="space-y-1">
          {iconsOnly ? null : <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--bo-faint)]">{section.title}</div>}
          {section.items.map((item) => {
            const Icon = item.icon;
            const tone = item.path === '/review' ? reviewBadgeTone(assigned) : null;
            return (
              <button
                key={item.path}
                type="button"
                title={tone ? `${item.label}, ${assigned} assigned` : item.label}
                onClick={() => go(item.path)}
                className={`relative flex w-full items-center rounded-lg text-sm ${iconsOnly ? 'justify-center px-2 py-2' : 'px-3 py-2'} ${location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)) ? 'bg-[#895CF6] font-medium text-white' : 'text-[var(--bo-muted)] hover:bg-[var(--bo-hover)] hover:text-[var(--bo-text)]'}`}
              >
                <Icon className={`h-5 w-5 shrink-0 ${iconsOnly ? '' : 'mr-3'}`} />
                {iconsOnly ? null : <span className="flex-1 text-left">{item.label}</span>}
                {tone ? (
                  <span className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white ${tone === 'red' ? 'bg-red-600' : 'bg-amber-500'} ${iconsOnly ? 'absolute -right-0.5 -top-0.5' : 'ml-2'}`}>{assigned}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );

  const footer = (iconsOnly: boolean) => (
    <div className={`border-t border-[var(--bo-border)] ${iconsOnly ? 'p-2' : 'p-3'}`}>
      <a href="/docs/" target="_blank" rel="noopener noreferrer" title="Documentation" className={`flex w-full items-center rounded-lg text-sm text-[var(--bo-muted)] hover:bg-[var(--bo-hover)] hover:text-[var(--bo-text)] ${iconsOnly ? 'justify-center px-2 py-2' : 'px-3 py-2'}`}>
        <BookOpen className={`h-5 w-5 shrink-0 ${iconsOnly ? '' : 'mr-3'}`} />
        {iconsOnly ? null : <span className="flex-1 text-left">Documentation</span>}
        {iconsOnly ? null : <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />}
      </a>
      {settingsItem ? (
        <button type="button" title="Settings" className={`flex w-full items-center rounded-lg text-sm text-[var(--bo-muted)] hover:bg-[var(--bo-hover)] hover:text-[var(--bo-text)] ${iconsOnly ? 'justify-center px-2 py-2' : 'px-3 py-2'} ${location.pathname.startsWith('/settings') ? 'bg-[#895CF6] text-white' : ''}`} onClick={() => go('/settings')}>
          <Settings className={`h-5 w-5 shrink-0 ${iconsOnly ? '' : 'mr-3'}`} />
          {iconsOnly ? null : 'Settings'}
        </button>
      ) : null}
    </div>
  );

  const brand = (showClose: boolean) => (
    <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-[var(--bo-border)] px-3">
      <button type="button" onClick={() => go('/')} aria-label="MaxIron Backoffice" className="flex items-center gap-2">
        <img src={dark ? '/maxiron-mark-white.svg' : '/maxiron-mark.svg'} alt="" className="h-8 w-auto" />
        <span className="leading-none">
          <span className="block text-sm font-bold tracking-[0.16em]">MAXIRON</span>
          <span className="mt-0.5 block text-[0.55rem] font-bold tracking-[0.22em] text-[#8F00FF]">BACKOFFICE</span>
        </span>
      </button>
      {showClose ? <button type="button" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden" aria-label="Close menu" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button> : null}
    </div>
  );

  const mode = navMode;
  const margin = mode === 'expanded' ? 'lg:ml-[210px]' : mode === 'thin' ? 'lg:ml-16' : 'lg:ml-0';
  const initials = (user?.name || user?.email || '?').split(/[\s@]/).map((part) => part[0]).slice(0, 2).join('').toUpperCase();
  function findPage() {
    const q = query.trim().toLowerCase();
    const hit = NAV.flatMap((section) => section.items).find((item) => item.label.toLowerCase().includes(q));
    if (hit) go(hit.path);
  }
  const navTitle = mode === 'expanded' ? 'Icon sidebar' : mode === 'thin' ? 'Hide sidebar' : 'Expand sidebar';
  const activity = useActivity();
  const pageCrumbs = usePageCrumbs();
  const trail = pageCrumbs.length ? pageCrumbs : [{ label: titleForUrl(location.pathname) }];

  return (
    <div className="flex min-h-screen bg-[var(--bo-canvas)] text-[var(--bo-text)]">
      {open ? <button className="fixed inset-0 z-40 bg-black/50 lg:hidden" aria-label="Close menu" onClick={() => setOpen(false)} /> : null}
      <aside className={`${open ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 flex w-[210px] flex-col border-r border-[var(--bo-border)] bg-[var(--bo-canvas)] transition-transform lg:hidden`}>
        {brand(true)}
        {links(false)}
        {footer(false)}
      </aside>
      {mode === 'expanded' ? (
        <aside className="fixed inset-y-0 left-0 z-50 hidden w-[210px] flex-col border-r border-[var(--bo-border)] bg-[var(--bo-canvas)] lg:flex">
          {brand(false)}
          {links(false)}
          {footer(false)}
        </aside>
      ) : null}
      {mode === 'thin' ? (
        <aside className="fixed inset-y-0 left-0 z-50 hidden w-16 flex-col border-r border-[var(--bo-border)] bg-[var(--bo-canvas)] lg:flex">
          <div className="flex h-14 items-center justify-center border-b border-[var(--bo-border)]">
            <img src={dark ? '/maxiron-mark-white.svg' : '/maxiron-mark.svg'} alt="" className="h-8 w-auto" />
          </div>
          {links(true)}
          {footer(true)}
        </aside>
      ) : null}
      {mode === 'hidden' ? (
        <div
          className={`fixed inset-y-0 left-0 z-50 hidden overflow-hidden transition-[width] duration-200 lg:flex ${peek ? 'w-[210px] border-r border-[var(--bo-border)] bg-[var(--bo-canvas)]' : 'w-3'}`}
          onMouseEnter={() => setPeek(true)}
          onMouseLeave={() => setPeek(false)}
        >
          {peek ? <>{brand(false)}{links(false)}{footer(false)}</> : null}
        </div>
      ) : null}
      <div className={`flex min-w-0 flex-1 flex-col ${margin}`}>
        <header className="sticky top-0 z-30 flex h-14 flex-shrink-0 items-center justify-between border-b border-[var(--bo-border)] bg-[var(--bo-canvas)] px-4">
          <div className="flex min-w-0 items-center gap-2">
            <button type="button" className="rounded-lg p-2 hover:bg-[var(--bo-hover)] lg:hidden" aria-label="Open menu" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <button type="button" className="hidden rounded-lg p-2 hover:bg-[var(--bo-hover)] lg:inline-flex" aria-label={navTitle} title={navTitle} onClick={cycleNav}><PanelLeft className="h-5 w-5" /></button>
            <CrumbTrail crumbs={trail} />
          </div>
          <div className="flex items-center gap-2">
            <form className="relative hidden sm:block" onSubmit={(event) => { event.preventDefault(); findPage(); }}>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--bo-faint)]" />
              <input aria-label="Search pages" className="h-9 w-56 rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] pl-9 pr-3 text-sm text-[var(--bo-text)] placeholder:text-[var(--bo-faint)]" placeholder="Search anything..." value={query} onChange={(event) => setQuery(event.target.value)} />
            </form>
            {can('chat') ? (
              <button type="button" aria-label="Invoke agents" aria-pressed={collaborateOpen} className="rounded-lg p-2 text-[var(--bo-muted)] hover:bg-[var(--bo-hover)]" onClick={() => (collaborateOpen ? closeCollaborate() : openCollaborate())}>
                <MessageSquare className="h-5 w-5" />
              </button>
            ) : null}
            <div className="relative" ref={notesRef}>
              <button type="button" aria-label="Notifications" aria-expanded={notesOpen} className="rounded-lg p-2 text-[var(--bo-muted)] hover:bg-[var(--bo-hover)]" onClick={() => setNotesOpen((open) => !open)}><Bell className="h-5 w-5" /></button>
              {notesOpen ? (
                <div className="absolute right-0 top-11 z-40 w-80 rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] shadow-lg">
                  <div className="border-b border-[var(--bo-border)] px-4 py-3 text-sm font-semibold">Notifications</div>
                  {activity.jobs.length ? activity.jobs.map((job) => (
                    <button key={job.id} type="button" className="block w-full px-4 py-3 text-left text-sm hover:bg-[var(--bo-hover)]" onClick={() => { activity.open(job.id); setNotesOpen(false); }}>
                      <div className="font-medium">{job.outcome === 'sent' ? 'Email sent' : job.outcome === 'failed' ? 'Email was not sent' : job.dockTitle}</div>
                      <div className="text-xs text-[var(--bo-muted)]">{job.subtitle}</div>
                    </button>
                  )) : <p className="px-4 py-6 text-sm text-[var(--bo-muted)]">No notifications.</p>}
                </div>
              ) : null}
            </div>
            <button type="button" aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} className="rounded-lg p-2 hover:bg-[var(--bo-hover)]" onClick={toggleTheme}>
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <DropdownMenu
              trigger={
                <button type="button" aria-label="User menu" className="flex items-center gap-2 rounded-lg px-1 py-1 hover:bg-[var(--bo-hover)]">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#895CF6] text-xs font-semibold text-white">{initials}</span>
                  <span className="hidden text-sm md:inline">{user?.name || 'Signed in'}</span>
                </button>
              }
              items={[
                { label: user?.name || 'Signed in', icon: <User className="h-4 w-4" />, onClick: () => undefined, className: 'pointer-events-none font-medium' },
                { label: user?.email || '', onClick: () => undefined, className: 'pointer-events-none text-xs text-gray-500' },
                { label: 'Sign out', icon: <LogOut className="h-4 w-4" />, className: 'text-red-600 dark:text-red-400', onClick: () => dirty.requestLeave(() => void logout().then(() => navigate('/login'))) },
              ]}
            />
          </div>
        </header>
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-24 pt-4 sm:px-6 lg:px-8 lg:pb-8">{children}</main>
        <CollaborateDock />
      </div>
      {dirty.pending ? (
        <Dialog
          title="Unsaved changes"
          onClose={dirty.stay}
          footer={
            <>
              <Button variant="outline" onClick={dirty.stay}>Stay</Button>
              <Button variant="ghost" onClick={dirty.discard}>Discard</Button>
              <Button onClick={() => void dirty.saveAndLeave()}>Save</Button>
            </>
          }
        >
          <p className="text-sm text-gray-600 dark:text-gray-300">Save or discard this edit before leaving.</p>
        </Dialog>
      ) : null}
    </div>
  );
}
